/**
 * Is the feed live, and what should that say on screen?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * "LIVE" MEANS TICKS ARE ARRIVING, NOT THAT A SOCKET IS OPEN.
 *
 * A socket in OPEN with nothing coming down it is exactly the state that
 * must not read as live — it is how a frozen price gets traded against. So
 * the judgement is made on the last frame, not on the connection.
 *
 * Outside market hours the right words are different again. The socket is
 * deliberately asleep (outside 09:15-15:40, see marketWindow) and the prices on
 * screen are the close. Calling that "offline" would be alarming and wrong:
 * nothing is broken.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { isConnectWindow } from '@apna/shared'

export type FeedState =
  /** Frames are arriving. */
  | { kind: 'LIVE' }
  /** The market is shut. Prices are the last close; nothing is wrong. */
  | { kind: 'CLOSED' }
  /** The market is open and we are NOT receiving. This one is a problem. */
  | { kind: 'OFFLINE'; lastTickAt: number | null }

/** How long without a frame before a live session counts as offline. */
export const SILENCE_MS = 10_000

export function feedStateOf(input: {
  socketState: string
  lastFrameAt: number
  now: number
  simulationActive?: boolean
}): FeedState {
  const open = input.simulationActive === true || isConnectWindow(new Date(input.now))
  if (!open) return { kind: 'CLOSED' }

  const heard = input.lastFrameAt > 0 && input.now - input.lastFrameAt < SILENCE_MS
  if (input.socketState === 'OPEN' && heard) return { kind: 'LIVE' }

  return { kind: 'OFFLINE', lastTickAt: input.lastFrameAt > 0 ? input.lastFrameAt : null }
}

/** The one line a tile's footer shows. */
export function tileStatusText(state: FeedState): string {
  switch (state.kind) {
    case 'LIVE':
      return 'Live'
    case 'CLOSED':
      return 'Last close · 15:30'
    case 'OFFLINE':
      return state.lastTickAt === null ? 'No feed' : `Stalled · ${hhmmss(state.lastTickAt)}`
  }
}

const hhmmss = (ms: number): string =>
  new Date(ms).toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour12: false,
  })

/** The market-session pill in the top bar. Independent of the socket. */
export function marketSessionText(now: number, simulationActive = false): { open: boolean; text: string } {
  if (simulationActive) return { open: true, text: 'Simulation open' }
  const open = isConnectWindow(new Date(now))
  return { open, text: open ? 'Market open' : 'Market closed' }
}

/**
 * How far behind real time the prices on screen are.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS FRAME AGE, NOT EXCHANGE LATENCY.
 *
 * It measures the gap between now and the last frame this tab received — how
 * stale the oldest thing on screen could be — NOT the broker's own lag behind
 * the exchange. We cannot measure that: `exchTs` is integer seconds at the
 * source ([B13]), so a sub-second difference against it would be reporting
 * its own rounding as latency.
 *
 * Frame age is the number that actually changes behaviour: a chain whose last
 * print was eight seconds ago is a chain you must not cross a spread into,
 * whatever the broker's internal latency was.
 *
 * OUTSIDE MARKET HOURS IT IS NOT A NUMBER AT ALL. The socket is deliberately
 * asleep outside 09:15-15:40, so the age would climb into hours and read as a
 * fault. Same judgement as `feedStateOf`: nothing is broken, so nothing
 * alarming is shown.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Above this, during market hours, the age stops being normal jitter. */
export const FEED_AGE_OK_MS = 2_000

export interface FeedAge {
  /** The chip's text, already formatted. */
  text: string
  /** False paints the dot red. Null means "no judgement" — a muted dot. */
  ok: boolean | null
  /** The full sentence, for the chip's title. */
  detail: string
}

export function feedAgeOf(input: { lastFrameAt: number; now: number; simulationActive?: boolean }): FeedAge {
  if (!input.simulationActive && !isConnectWindow(new Date(input.now))) {
    return {
      text: '—',
      ok: null,
      detail: 'The market is closed, so the feed is deliberately asleep. Prices are the last close.',
    }
  }

  if (input.lastFrameAt <= 0) {
    return { text: '—', ok: false, detail: 'No market data has arrived in this session yet.' }
  }

  const ageMs = Math.max(0, input.now - input.lastFrameAt)
  const ok = ageMs < FEED_AGE_OK_MS
  // One decimal below ten seconds, whole seconds above: past ten the exact
  // tenth is noise and the magnitude is the whole message.
  const seconds = ageMs / 1_000
  const text = seconds < 10 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds)}s`
  return {
    text,
    ok,
    detail: ok
      ? `Last market-data frame ${text} ago.`
      : `Last market-data frame ${text} ago — the prices on screen are at least that stale.`,
  }
}
